import type {DesignerStores} from '@packages/systems/designer/flux';
import {selectCurrentExperienceId} from '@packages/systems/optimization/store';
import {Component, Expressions} from '@packages/systems/wfdl';
import {Expression} from '@packages/systems/wfdl-ast';
import type {Qualified} from '@packages/systems/wfdl-Qualified';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {isOptimizationVariationElement} from '@packages/utilities/platform';
import {
  getLiveComponentMap,
  selectCurrentComponentRender,
  selectCurrentComponentType,
} from '@packages/utilities/platform-selectors';
import {createSelector} from 'reselect';
import {getExperienceIds} from './getExperienceIds';
import {getSelectedElementForOptimization} from './getSelectedElementForOptimization';

export function getIsSelectedElementInAnotherExperience(
  nativeId: NativeId | null,
  nativeIdPath: NativeId[] | null | undefined,
  componentMap: Component.ComponentMap,
  currentComponentType: Qualified<Component.ComponentName>,
  currentComponentRender: Expression | null,
  currentExperienceId: string | null
): boolean {
  if (!currentComponentRender || !currentExperienceId) {
    return false;
  }

  const selectedElement = getSelectedElementForOptimization(
    nativeId,
    nativeIdPath,
    componentMap,
    currentComponentType,
    currentComponentRender
  );

  if (!selectedElement) {
    return false;
  }

  const closestVariationElement = Expressions.closestElement(
    currentComponentRender,
    selectedElement.id,
    isOptimizationVariationElement
  );
  if (!closestVariationElement) {
    return false;
  }
  const experienceIds = getExperienceIds(closestVariationElement);
  const isInAnotherExperience = Array.from(experienceIds).some(
    (id) => id !== currentExperienceId
  );
  return isInAnotherExperience;
}

export const selectIsSelectedElementInAnotherExperience = createSelector(
  (stores: DesignerStores) => stores.UiNodeStore.selectedNodeNativeId,
  (stores: DesignerStores) => stores.UiNodeStore.selectedNativeIdPath,
  (stores: DesignerStores) => getLiveComponentMap(stores.DesignerStore),
  (stores: DesignerStores) => selectCurrentComponentType(stores),
  selectCurrentComponentRender,
  (stores: DesignerStores) =>
    selectCurrentExperienceId(stores.OptimizationStore),
  getIsSelectedElementInAnotherExperience
);
